import React, { useEffect, useMemo, useState } from 'react';
import { formatMoney } from 'utils/storefront/format-resource';
import QuantityInput from 'utils/quantity-input';
import VariantSelector from './variant-selector';
import DiscountTiers from './discount-tiers';
import { useCartContext } from '../cart/cart-context';
import ProductReviewStars from './product-review-stars';
import ProductSubBrand from './product-subbrand';
import ProductTextBanner from './product-text-banner';
import VariantValueProps from './product-props';
import { useProduct } from './product-context';
import { trackAddToCart } from '../utils/tracking';
import DiscountTag from '../discount-tag';
import ProductCustomTextBanner from './product-custom-banner';

const Price = ({ currentVariant, product }) => {
  let price = product.priceRange.minVariantPrice;
  let compareAtPrice = product.compareAtPriceRange.maxVariantPrice;

  if (currentVariant) {
    price = currentVariant.price;
    compareAtPrice = currentVariant.compareAtPrice;
  }

  return (
    <div className="h5 flex flex-none gap-1">
      {parseFloat(compareAtPrice.amount) > 0 && <del className="text-grey-dark">{formatMoney(price)}</del>}
      <span className={parseFloat(compareAtPrice.amount) > 0 ? 'text-sale' : ''}>
        {formatMoney(compareAtPrice.amount > 0 ? compareAtPrice : price)}
      </span>
    </div>
  );
};

const AddToCartButton = ({
  currentVariant,
  quantity,
  selectedOptions,
  product,
  setAddToCartQuantity,
  variantIsOos,
}) => {
  const handleAddToCart = (e) => {
    e.preventDefault();

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          merchandiseId: currentVariant.id,
          quantity,
        },
      },
    });
    document.dispatchEvent(addToCartEvent);

    const compareAtPrice = currentVariant.compareAtPrice?.amount || 0;
    const price = currentVariant.price.amount;
    const discountValue = compareAtPrice > price ? compareAtPrice - price : 0;

    window.runIfTargetingConsent(() => {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: currentVariant.price?.currencyCode || 'USD',
          value: currentVariant.price.amount * 1,
          items: [
            {
              item_id: currentVariant.sku || currentVariant.id,
              item_name: product.title,
              affiliation: '',
              coupon: '',
              discount: discountValue,
              index: 0,
              item_brand: product.vendor,
              item_category: product.collections?.[0] || '',
              item_category2: product.collections?.[1] || '',
              item_category3: product.collections?.[2] || '',
              item_category4: product.collections?.[3] || '',
              item_category5: product.collections?.[4] || '',
              item_variant: currentVariant.title,
              location_id: '',
              price: currentVariant.price.amount,
              quantity: 1,
            },
          ],
        },
      });
      try {
        window.optimizely = window.optimizely || [];
        window.optimizely.push({
          type: 'event',
          eventName: 'add_to_cart',
          tags: {
            revenue: Math.round((currentVariant.price.amount || 0) * 100),
            value: currentVariant.price.amount * quantity,
            currency: currentVariant.price?.currencyCode || 'USD',
            item_id: currentVariant.sku || currentVariant.id,
            item_name: product.title,
            quantity,
          },
        });
      } catch (err) {
        console.warn('Optimizely push failed', err);
      }
    });

    trackAddToCart({
      product,
      variant: currentVariant,
      quantity,
    });

    setAddToCartQuantity(0);
  };

  const label = useMemo(() => {
    if (selectedOptions.some((o) => o.value === null)) {
      return window.TMW.Strings.product.selectSize;
    }
    if (!currentVariant) {
      return window.TMW.Strings.product.unavailable;
    }
    if (variantIsOos) {
      return window.TMW.Strings.product.outOfStock;
    }
    return window.TMW.Strings.product.addToCart;
  }, [currentVariant, selectedOptions]);

  return (
    <button
      type="button"
      className="button button--primary flex-1 whitespace-nowrap"
      disabled={variantIsOos}
      onClick={(e) => handleAddToCart(e)}
    >
      {label}
    </button>
  );
};

const ProductForm = ({ loadingCartProducts, cartLoading, consentCode, errorText }) => {
  const { cart } = useCartContext();
  const { product, currentVariant, setSelectedOptions, selectedOptions, setCurrentVariant } = useProduct();
  const { discountTagMessage, discountTagBgColor, discountTagMessageColor } = product.metafields;
  const hasBothSaleAndDiscountTag = currentVariant?.metafields?.finalSale && discountTagMessage;
  // tracks the quantity the user has entered in the form
  const [formQuantity, setFormQuantity] = useState(1);
  // tracks the quantity that will be added to the cart so that we never try to add more than inventoryQuantity to cart. workaround for shopify cart API issue
  const [addToCartQuantity, setAddToCartQuantity] = useState(0);
  const variantIsOos = currentVariant?.inventoryQuantity <= 10 || !currentVariant?.availableForSale;

  let availableInventory;

  if (currentVariant) {
    const inventoryTreshold = currentVariant.inventoryQuantity - 10;
    if (inventoryTreshold > 0) {
      availableInventory = inventoryTreshold;
    } else {
      availableInventory = 0;
    }
  } else {
    availableInventory = 0;
  }

  const matchingCartItem = cart.state.items?.find((item) => {
    const currentVariantId = Number(currentVariant?.id.match(/[^/]+$/)[0]);
    return currentVariantId === item.variant_id;
  });

  // checks, resets form quantity against inventory quantity when a user changes from a variant with high-count inventory to a variant with low-count inventory
  useEffect(() => {
    const formQuantityExeedsInventory = formQuantity > availableInventory;
    if (formQuantityExeedsInventory && availableInventory >= 1) {
      setFormQuantity(availableInventory);
    }
    if (formQuantityExeedsInventory && availableInventory < 1) {
      setFormQuantity(1);
    }
  }, [availableInventory]);

  // considers if variant is in cart, form quantity and available inventory quantity to adjust the quantity that will be added to cart. prevents adding more than available inventory to the cart as a workaround for shopify cart API issue
  useEffect(() => {
    if (matchingCartItem) {
      const formAndCartQuantity = formQuantity + matchingCartItem.quantity;

      if (formAndCartQuantity <= availableInventory) {
        setAddToCartQuantity(formQuantity);
      } else {
        const quantityToAdd = availableInventory - matchingCartItem.quantity;

        if (quantityToAdd > 0) {
          setAddToCartQuantity(quantityToAdd);
        } else {
          setAddToCartQuantity(0);
        }
      }
    } else if (formQuantity <= availableInventory) {
      setAddToCartQuantity(formQuantity);
    } else if (formQuantity > availableInventory) {
      setAddToCartQuantity(availableInventory);
    }
  }, [formQuantity, availableInventory, matchingCartItem]);

  const handleOptionChange = (option, value) => {
    if (option.name.toLowerCase() === 'color') {
      const newSelectOptions = selectedOptions.map((o) => (o.name === option.name ? { ...o, value } : o));

      const matchingOption = product.variants.find(
        (v) => JSON.stringify(v.selectedOptions) === JSON.stringify(newSelectOptions),
      );
      if (matchingOption) {
        setSelectedOptions((prev) => prev.map((o) => (o.name === option.name ? { ...o, value } : o)));
      } else {
        setSelectedOptions((prev) =>
          prev.map((o) => (o.name === option.name ? { ...o, value } : { ...o, value: null })),
        );
        setCurrentVariant(null);
        if (product.url) window.history.replaceState({}, '', `${product.url}`);
      }
    } else {
      setSelectedOptions((prev) => prev.map((o) => (o.name === option.name ? { ...o, value } : o)));
    }
  };

  const bundleText = useMemo(() => {
    if (!product.tags?.includes('thinx-set')) return null;
    if (!product.metafields?.saverSetList.length) return null;

    return (
      <div>
        <h3
          className="body-2 mb-1 font-normal"
          dangerouslySetInnerHTML={{ __html: window.TMW.Strings.product.bundleTextTitle }}
        ></h3>
        <ul className="list-image-[--disc] [&>:last-child]:mb-0">
          {product.metafields?.saverSetList.map((item, i) => (
            <li
              key={`bundle-item-${i}`}
              className="body-2 mb-1 ml-3.5 pl-1.5"
              dangerouslySetInnerHTML={{ __html: item }}
            ></li>
          ))}
        </ul>
      </div>
    );
  }, []);

  const variantSelectors = useMemo(
    () =>
      !product.hasOnlyDefaultVariant &&
      product.options.map((option) => (
        <VariantSelector
          currentVariant={currentVariant}
          key={option.name}
          option={option}
          selectedOptions={selectedOptions}
          onChange={handleOptionChange}
          variants={product.variants}
          product={product}
        />
      )),
    [selectedOptions],
  );

  const price = useMemo(
    () => (
      <Price
        currentVariant={currentVariant}
        product={product}
      />
    ),
    [currentVariant, product],
  );

  const variantValueProps = useMemo(() => <VariantValueProps currentVariant={currentVariant} />, [currentVariant]);

  const sizeGuideTrigger = useMemo(
    () => (
      <div>
        <button
          className="link"
          data-size-guide-trigger
          role="button"
          type="button"
        >
          {window.TMW.Strings.sizeGuide.title}
        </button>
      </div>
    ),
    [],
  );

  // Update the URL with the new variant ID upon a variant change
  // Persist other query strings (if any) and place the variant ID parameter in front of other parameters
  useEffect(() => {
    if (currentVariant && product.url) {
      const { search } = new URL(window.location);
      const searchParams = new URLSearchParams(search);
      searchParams.delete('variant');
      const paramsWithoutVariant = searchParams.toString();
      const variantId = currentVariant.id.split('/ProductVariant/')[1];
      const organizedParamsString = `?variant=${variantId}${paramsWithoutVariant && `&${paramsWithoutVariant}`}`;

      window.history.replaceState({}, '', `${product.url}${organizedParamsString}`);
    }
  }, [currentVariant, product.url]);

  return (
    <>
      <form
        method="post"
        action="/cart/add"
        acceptCharset="UTF-8"
        encType="multipart/form-data"
        noValidate="novalidate"
        data-product-form
        onSubmit={(e) => e.preventDefault()}
        className="flex flex-col gap-6"
      >
        <div className="flex flex-col gap-y-2">
          <div className={`relative flex justify-between ${hasBothSaleAndDiscountTag ? 'h-[50px] items-end' : ''}`}>
            <ProductSubBrand product={product} />
            {discountTagMessage && (
              <DiscountTag
                className="absolute right-0 top-0"
                message={discountTagMessage}
                bgColor={discountTagBgColor}
                textColor={discountTagMessageColor}
              />
            )}
            {currentVariant?.metafields?.finalSale && (
              <span className="body-3 text-sale">{window.TMW.Strings.product.finalSale}</span>
            )}
          </div>
          <div className="flex gap-4">
            <h1 className="h4 flex-1">{product.title}</h1>
            {price}
          </div>
          <ProductTextBanner />
          <ProductReviewStars
            productId={product.id}
            consentCode={consentCode}
            errorText={errorText}
          />
          {bundleText}
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-5">{variantSelectors}</div>
          {product.showSizeGuide && sizeGuideTrigger}
        </div>
        <ProductCustomTextBanner />
        <div className="flex flex-col gap-3">
          {!loadingCartProducts && product.tags?.includes('custom-set-eligible') && (
            <DiscountTiers
              product={product}
              cart={cart}
            />
          )}

          <div className="flex gap-1">
            <div
              className={`w-[6.875rem] flex-none ${
                !variantIsOos ? 'w-[6.875rem] flex-none' : 'pointer-events-none opacity-50'
              }`}
            >
              <QuantityInput
                quantity={formQuantity}
                title={window.TMW.Strings.cart.quantity}
                setQuantity={setFormQuantity}
                cartLoading={cartLoading}
                max={availableInventory}
              />
            </div>

            <AddToCartButton
              currentVariant={currentVariant}
              quantity={addToCartQuantity}
              selectedOptions={selectedOptions}
              product={product}
              setAddToCartQuantity={setAddToCartQuantity}
              variantIsOos={variantIsOos}
            />
          </div>

          {!!availableInventory && availableInventory < 10 && (
            <p className="body-3 text-sale">
              {window.TMW.Strings.cart.maxQuantityError?.replace('{quantity}', availableInventory)}
            </p>
          )}

          {currentVariant?.metafields?.finalSale && (
            <p className="body-3 text-sale">{window.TMW.Strings.product.finalSaleLong}</p>
          )}
          {variantValueProps}
        </div>
      </form>
    </>
  );
};

export default ProductForm;
